import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import { Skeleton } from "@/components/ui/skeleton";

interface AssignProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignProductSearch: string;
  setAssignProductSearch: (value: string) => void;
  assignProductCode: string;
  setAssignProductCode: (value: string) => void;
  assignProductMerchant: string;
  setAssignProductMerchant: (value: string) => void;
  assignProductBrand: string;
  setAssignProductBrand: (value: string) => void;
  assignProductPage: number;
  assignProductPageSize: number;
  assignProductTotal: number;
  assignProductLoading: boolean;
  assignProductRows: any[];
  assignProductSelected: number[];
  setAssignProductSelected: (selected: number[]) => void;
  showAssignConfirm: boolean;
  setShowAssignConfirm: (show: boolean) => void;
  assigning: boolean;
  merchants: any[];
  brands: any[];
  selectedCategoryId: string;
  onAssign: () => void;
  onReset: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const AssignProductDialog: React.FC<AssignProductDialogProps> = ({
  open,
  onOpenChange,
  assignProductSearch,
  setAssignProductSearch,
  assignProductCode,
  setAssignProductCode,
  assignProductMerchant,
  setAssignProductMerchant,
  assignProductBrand,
  setAssignProductBrand,
  assignProductPage,
  assignProductPageSize,
  assignProductTotal,
  assignProductLoading,
  assignProductRows,
  assignProductSelected,
  setAssignProductSelected,
  showAssignConfirm,
  setShowAssignConfirm,
  assigning,
  merchants,
  brands,
  selectedCategoryId,
  onAssign,
  onReset,
  onPageChange,
  onPageSizeChange,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-full p-0 rounded-2xl shadow-xl border bg-white">
        <div className="rounded-2xl overflow-hidden">
          {/* Confirmation bar */}
          {assignProductSelected.length > 0 && showAssignConfirm && (
            <div className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b border-gray-200 rounded-t-2xl">
              <span className="font-semibold text-sm text-gray-800">
                Are you sure you want to assign these products to category{" "}
                {selectedCategoryId}?
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={assigning}
                  onClick={onAssign}
                  aria-label="Confirm"
                >
                  ✓
                </Button>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-lg font-bold text-gray-800">Choose Product</h2>
          </div>
          {/* Filters row */}
          <div className="flex flex-wrap gap-2 mb-4 items-end px-6 pt-4">
            <Input
              placeholder="Search"
              value={assignProductSearch}
              onChange={(e) => setAssignProductSearch(e.target.value)}
              className="w-48"
            />
            <Input
              placeholder="Product code"
              value={assignProductCode}
              onChange={(e) => setAssignProductCode(e.target.value)}
              className="w-40"
            />
            <Select
              value={assignProductMerchant}
              onValueChange={setAssignProductMerchant}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select merchant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All merchants</SelectItem>
                {merchants.map((merchant) => (
                  <SelectItem key={merchant.id} value={merchant.id.toString()}>
                    {merchant.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={assignProductBrand}
              onValueChange={setAssignProductBrand}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id.toString()}>
                    {brand.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={onReset}>
              Reset
            </Button>
          </div>
          {/* Table */}
          <div className="overflow-x-auto px-6 pb-2 max-h-96 overflow-y-auto">
            <table className="min-w-full border rounded-xl overflow-hidden bg-white">
              <thead className="sticky top-0 z-10">
                <tr className="text-left text-gray-600 text-xs border-b bg-gray-50">
                  <th className="py-2 px-2">
                    <input
                      type="checkbox"
                      checked={
                        assignProductSelected.length ===
                          assignProductRows.length &&
                        assignProductRows.length > 0
                      }
                      onChange={(e) =>
                        setAssignProductSelected(
                          e.target.checked
                            ? assignProductRows.map((r) => r.id)
                            : []
                        )
                      }
                    />
                  </th>
                  <th className="py-2 px-2">Create Date</th>
                  <th className="py-2 px-2">Logo</th>
                  <th className="py-2 px-2">Brand</th>
                  <th className="py-2 px-2">Merchant</th>
                  <th className="py-2 px-2">Product Code</th>
                  <th className="py-2 px-2">Product Name</th>
                  <th className="py-2 px-2">Shipped From</th>
                  <th className="py-2 px-2">Shipping Profiles</th>
                </tr>
              </thead>
              <tbody>
                {assignProductLoading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8">
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                      <Skeleton className="h-8 w-full mb-2" />
                    </td>
                  </tr>
                ) : assignProductRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-xs">
                      No products found
                    </td>
                  </tr>
                ) : (
                  assignProductRows.map((row) => (
                    <tr
                      key={row.id}
                      className="bg-white border-b hover:bg-gray-50 transition text-xs"
                    >
                      <td className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={assignProductSelected.includes(row.id)}
                          onChange={(e) => {
                            setAssignProductSelected((sel) =>
                              e.target.checked
                                ? [...sel, row.id]
                                : sel.filter((id) => id !== row.id)
                            );
                            setShowAssignConfirm(false);
                          }}
                        />
                      </td>
                      <td className="py-2 px-2">
                        {row.createdDate
                          ? new Date(row.createdDate).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "-"}
                      </td>
                      <td className="py-2 px-2">
                        <img
                          src={row.productImageUri || "/placeholder.svg"}
                          alt={row.productName}
                          className="w-12 h-12 object-contain rounded border bg-gray-50"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                        />
                      </td>
                      <td className="py-2 px-2">
                        {row.brandVO?.brandName || "-"}
                      </td>
                      <td className="py-2 px-2">
                        {row.redemptionMerchantVO?.merchantName || "-"}
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-blue-600 underline cursor-pointer hover:text-blue-800 transition">
                          {row.productCode}
                        </span>
                      </td>
                      <td className="py-2 px-2">{row.productName}</td>
                      <td className="py-2 px-2">
                        {row.redemptionMerchantVO?.countryVO?.name || "-"}
                      </td>
                      <td className="py-2 px-2">
                        {row.redemptionMerchantVO.assignedShippingProfiles
                          ?.map((sp) => sp.name)
                          .join(", ") || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-6 pb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Rows per page:</span>
              <Select
                value={assignProductPageSize.toString()}
                onValueChange={(v) => onPageSizeChange(Number(v))}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={assignProductPageSize.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <PaginationControls
              totalPages={Math.ceil(assignProductTotal / assignProductPageSize)}
              currentPage={assignProductPage}
              onNextHandler={() =>
                onPageChange(
                  Math.min(
                    Math.ceil(assignProductTotal / assignProductPageSize) - 1,
                    assignProductPage + 1
                  )
                )
              }
              onPrevHandler={() =>
                onPageChange(Math.max(0, assignProductPage - 1))
              }
              onPageChange={onPageChange}
            />
          </div>
          {/* Assign button */}
          {assignProductSelected.length > 0 && !showAssignConfirm && (
            <div className="flex justify-end px-6 pb-2">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setShowAssignConfirm(true)}
                disabled={assigning}
              >
                Assign
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
